/* global ifCl */
import React, { useContext, useState, useEffect } from "react";
import styled from "styled-components";
import ActionsContext from "common/ActionsContext";
import Request from "common/Request";
import SelectInputWithIcon from "common/SelectInputWithIcon";
import {
  parsePolicyNames,
  getCmdSubscriber,
  getCmdSubscribergroup,
  parseApplicationMetrics,
  getPolicyFilterCmd,
} from "views/Statistics/DPI/ApplicationMetrics/api";
import SelectMultiAutocomplete from "common/SelectMultiAutocomplete";
import { getBqnIdCmdPart, safeStr } from "common/api";
import Switch from "@mui/material/Switch";
import BqnContext from "common/BqnContext";
import TimeChart from "views/Statistics/SubscriberDetail/DPITimeChart";
import { AppChart } from "views/Statistics/DPI/ApplicationMetrics/utils"

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: flex-end;
  align-items: center;
`;

const MultiSelectorsWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  & > div {
    width: 100%;
    margin-right: 10px;
  }
  @media (max-width: 800px) {
    flex-direction: column;
    & > div {
      width: 100%;
      margin-right: 0px;
    }
  }
`;

const WrapperAppCharts = styled.div`
  .split {
    h4 {
      margin-bottom: 26px !important;

      @media screen and (max-width: 990px) {
        margin-bottom: 15px !important;
      }
    }
  }
  h4.subtitle {
    font-size: 14px;
    margin: 0px;
    padding: 10px 0;
  }
`;

const LayoutRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 20px;

  @media (max-width: 750px) {
    flex-direction: column;
    align-items: flex-end;
    flex-wrap: wrap;

    .displaySelect {
      margin-right: 30px;
    }
  }
`;

const Wrapper = styled.div`
  width: 100%;
  .sw-input.MuiSwitch-root {
    & .Mui-checked {
      color: var(--switch-checked-green);
    }

    & .Mui-checked + .MuiSwitch-track {
      background-color: var(--switch-checked-light-green);
    }
  }
`;

const Header = styled.div`
  margin-top: 20px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;

  h4 {
    padding: 0 20px 0 0 !important;
    white-space: nowrap;
  }
`;

const ContextMenuDiv = styled.div`
  i {
    color: #999;
    font-size: 20px;

    &:hover {
      color: #000;
    }
  }
`;

const SwitchWithLabel = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const Label = styled.div`
  margin-right: 10px;
  font-family: AllianceNo2-SemiBold !important;
  font-weight: 100;
  color: var(--Text-Primary);
  width: 100px;
  text-align: right;
`;

const SelectWrapper = styled.div`
  width: 250px;
`;

const SwitchWrapper = styled.div`
  width: auto;
  display: flex;
  align-items: center;
  position: relative;
`;

const WrapperCharts = styled.div`
  width: 100%;
  padding: 0 20px;
`;

const _displayChoices = [
  { value: "updown", label: "Downlink & Uplink Speed per Flow" },
  { value: "downall", label: "Downlink Speed & Time Evolution" },
  { value: "upall", label: "Uplink Speed & Time Evolution" },
];

const titles = {
  downlink: "Downlink speed per flow",
  uplink: "Uplink speed per flow",
};

const AppCharts = ({
  data,
  plotMode,
  display,
  bqn='all',
  hours,
  subscriberGroup,
  target,
  showMinMax,
  policiesRate,
}) => {
  const { downlink, uplink } = data;
  const [filteredAppsDown, setFilteredAppsDown] = useState(
    new Set()
  );
  const [filteredAppsUp, setFilteredAppsUp] = useState(new Set());
  useEffect(() => {
    const appsDownlink = data.downlink.map((item) => item.app);
    setFilteredAppsDown(new Set(appsDownlink));
  }, [data.downlink]);

  useEffect(() => {
    const appsUplink = data.uplink.map((item) => item.app);
    setFilteredAppsUp(new Set(appsUplink));
  }, [data.uplink]);

  return (
    <WrapperAppCharts key={bqn}>
      <AppChart
        title={
          display === "downall" || display === "updown"
            ? titles.downlink
            : titles.uplink
        }
        data={display === "downall" || display === "updown" ? downlink : uplink}
        plotMode={plotMode}
        selection={
          display === "downall" || display === "updown"
            ? filteredAppsDown
            : filteredAppsUp
        }
        setSelection={
          display === "downall" || display === "updown"
            ? setFilteredAppsDown
            : setFilteredAppsUp
        }
        showMinMax={showMinMax}
      />
      {display === "updown" ? (
        <AppChart
          title={titles.uplink}
          data={data.uplink}
          plotMode={plotMode}
          selection={filteredAppsUp}
          setSelection={setFilteredAppsUp}
          showMinMax={showMinMax}
        />
      ) : null}
      {display === "downall" || display === "upall" ? (
        <TimeChart
          display={display}
          data={display === "downall" ? downlink : uplink}
          hours={hours}
          subscriberGroups={[subscriberGroup]}
          target={target}
          bqn={bqn}
          plotMode={plotMode}
          showMinMax={showMinMax}
          policiesRate={policiesRate}
        />
      ) : null}
    </WrapperAppCharts>
  );
};

const AppChartsBqns = ({
  data,
  plotMode,
  display,
  hours,
  target,
  subscriberGroup,
  showMinMax,
  policiesRate,
}) => {
  const {
    clusterStatus: { mode },
    bqnId,
  } = useContext(BqnContext);
  const entries = Object.entries(data);

  if (entries.length === 0) {
    return <h5 className="text-center margin-t-30">No data available</h5>;
  }
  const relevantBqns = mode === "cluster" && bqnId === 'all'
    ? () => true
    : ([bqn]) => parseFloat(bqn) === parseFloat(bqnId)
  const filteredEntries = entries.filter(relevantBqns);

  return  (<>
  {filteredEntries.map(([bqn, value]) =>
      <AppCharts
        key={bqn}
        data={value}
        plotMode={plotMode}
        display={display}
        bqn={bqn}
        hours={hours}
        subscriberGroup={subscriberGroup}
        target={target}
        showMinMax={showMinMax}
        mode={mode}
        policiesRate={policiesRate}
      />
      )}
      </>
    );
};

const SubscriberGroupDPISpeed = ({
  hours=24,
  test = false,
  target,
  subscriberGroup,
}) => {
  const [request, setRequest] = useState(null);
  const [plotMode, setPlotMode] = useState("boxplot");
  const [display, setDisplay] = useState("downall");
  const [showMinMax, setShowMinMax] = useState(false);
  const actions = useContext(ActionsContext);
  const [policiesRate, setPoliciesRate] = useState([]);
  const [policiesRateSelected, setPoliciesRateSelected] = useState([]);

  const {
    clusterStatus: { mode },
    bqnId,
  } = useContext(BqnContext);

  useEffect(() => {
    doLoad();

    return actions.recv("do-load", doLoad);
  }, [hours, policiesRateSelected.join(","), subscriberGroup, bqnId]);

  useEffect(() => {
    doLoadFilters();
  }, []);

  function doLoadFilters() {
    return ifCl.run("show policy rate").then((policiesResp) => {
      setPoliciesRate(parsePolicyNames(policiesResp));
    });
  }

  function doLoad() {
    const request =ifCl.run(
            `show pkteng app-metrics hours ${hours} categories 19 subscriber ${getCmdSubscriber(
              target
            )} policy ${getPolicyFilterCmd(
              policiesRateSelected
            )} subscriber-group ${getCmdSubscribergroup(
              subscriberGroup
            )} percentile${getBqnIdCmdPart(mode, bqnId)}`,
            { timeout: DPI_STATS_TMOUT_MS }
          )
      .then((response) => parseApplicationMetrics(response, mode, bqnId));

    setRequest(request);
  }

  return (
    <Wrapper>
      <Header>
        <h4>Application Speed Metrics</h4>
        <HeaderRight>
          <MultiSelectorsWrapper>
            <SelectMultiAutocomplete
              icon="folder_open"
              title="Rate policy"
              name="policy-rate-selector"
              placeholder="Rate policy"
              options={policiesRate.map((name) => {
                return { label: name, value: name };
              })}
              key="policyRateSelector"
              onChange={(value) =>
                setPoliciesRateSelected(
                  value.map((optionType) => optionType.value)
                )
              }
            />
          </MultiSelectorsWrapper>
          <ContextMenuDiv className="context-menu">
            <a
              onClick={() => doLoad()}
              data-toggle="cardloading"
              data-loading-effect="pulse"
              title="Refresh"
            >
              <i className="material-icons">refresh</i>
            </a>
          </ContextMenuDiv>
        </HeaderRight>
      </Header>
      <LayoutRow>
        <SelectWrapper className="displaySelect">
          <SelectInputWithIcon
            title="Display"
            name="Display"
            icon="apps"
            selected={display}
            onChange={({ target }) => setDisplay(target.value)}
            options={_displayChoices}
          />
        </SelectWrapper>
        <SwitchWrapper>
          <SwitchWithLabel>
            <Label>Line chart view</Label>{" "}
            <Switch
              className="sw-input"
              checked={plotMode !== "boxplot"}
              onChange={() =>
                setPlotMode(plotMode === "boxplot" ? "line" : "boxplot")
              }
            />
          </SwitchWithLabel>
          <SwitchWithLabel>
            <Label>View max+min</Label>{" "}
            <Switch
              className="sw-input"
              checked={showMinMax}
              onChange={() => setShowMinMax(!showMinMax)}
            />
          </SwitchWithLabel>
        </SwitchWrapper>
      </LayoutRow>

      <Request during={request}>
        {(result) => (
          <WrapperCharts>
            <AppChartsBqns
              data={result}
              plotMode={plotMode}
              display={display}
              hours={hours}
              target={target}
              subscriberGroup={subscriberGroup}
              showMinMax={showMinMax}
              policiesRate={policiesRateSelected}
            />
          </WrapperCharts>
        )}
      </Request>
    </Wrapper>
  );
};

export default SubscriberGroupDPISpeed;
